export interface OfflinePaymentDetails {
  // Indian payment methods
  upiId?: string;
  recipientName?: string;
  qrCodeImage?: string;

  // Worldwide payment methods
  walletAddress?: string;

  // Common fields
  additionalInfo?: string;
}

export interface OfflinePaymentMethod {
  id: string;
  title: string;
  icon: string;
  badge: string;
  category: 'indian' | 'worldwide';
  details: OfflinePaymentDetails;
  is_active?: boolean;
}

export interface OfflinePaymentConfig {
  paymentConfig: {
    indian: {
      [methodType: string]: OfflinePaymentMethod;
    };
    worldwide: {
      [methodType: string]: OfflinePaymentMethod;
    };
  };
  metadata: {
    version: string;
    lastUpdated: string;
    supportedCategories: string[];
    supportedMethods: {
      indian: string[];
      worldwide: string[];
    };
  };
}

export interface OfflinePaymentMethodFormData {
  category: 'indian' | 'worldwide';
  methodType: string;
  title: string;
  icon: string;
  badge: string;
  details: OfflinePaymentDetails;
}

export interface OfflinePaymentMethodUpdateRequest {
  category: 'indian' | 'worldwide';
  methodType: string;
  title?: string;
  icon?: string;
  badge?: string;
  details: Partial<OfflinePaymentDetails>;
}

export interface OfflinePaymentMethodAddRequest {
  category: 'indian' | 'worldwide';
  methodType: string;
  paymentMethod: OfflinePaymentMethod;
}

export interface OfflinePaymentMethodResponse {
  success: boolean;
  data?: OfflinePaymentConfig['paymentConfig'];
  message?: string;
  timestamp: string;
}

export interface OfflinePaymentMethodError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: string;
  };
  timestamp: string;
}

// Validation rules
export interface ValidationRule {
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  message: string;
}

export interface ValidationRules {
  indian: {
    required: string[];
    optional: string[];
    validation: {
      upiId: ValidationRule;
      recipientName: ValidationRule;
      qrCodeImage: ValidationRule;
    };
  };
  worldwide: {
    required: string[];
    optional: string[];
    validation: {
      walletAddress: ValidationRule;
      recipientName: ValidationRule;
      qrCodeImage: ValidationRule;
    };
  };
}

export const validationRules: ValidationRules = {
  indian: {
    required: ['qrCodeImage'],
    optional: ['upiId', 'recipientName', 'additionalInfo'],
    validation: {
      upiId: {
        pattern: '^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+$',
        message: 'UPI ID must be in valid format (e.g., user@bank)',
      },
      recipientName: {
        minLength: 2,
        maxLength: 50,
        message: 'Recipient name must be between 2-50 characters',
      },
      qrCodeImage: {
        pattern: '^/images/.*\\.(jpeg|jpg|png)$',
        message: 'QR code image must be a valid image path',
      },
    },
  },
  worldwide: {
    required: ['qrCodeImage'],
    optional: ['walletAddress', 'recipientName', 'additionalInfo'],
    validation: {
      walletAddress: {
        minLength: 20,
        maxLength: 100,
        message: 'Wallet address must be between 20-100 characters',
      },
      recipientName: {
        minLength: 2,
        maxLength: 50,
        message: 'Recipient name must be between 2-50 characters',
      },
      qrCodeImage: {
        pattern: '^/images/.*\\.(jpeg|jpg|png)$',
        message: 'QR code image must be a valid image path',
      },
    },
  },
};
